import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { register } from '../services/authService';
import type { RegisterData } from '../services/authService';
import { validatePassword, passwordsMatch } from '../utils/passwordValidation';
import { checkEmailDomain } from '../utils/emailDomainValidation';
import type { PasswordRequirements as PasswordRequirementsType } from '../utils/passwordValidation';
import PasswordRequirementsComponent from '../components/PasswordRequirements';
import { TURNSTILE_SITE_KEY } from '../config/env';
import './Register.css';

function Register() {
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    documento: '',
    email: '',
    domicilio: '',
    celular: '',
    birthdate: '',
    genero: '',
    password: '',
    confirmPassword: '',
  });
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirementsType>({
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasMinLength: false,
    allRequirementsMet: false,
  });
  const [passwordMatch, setPasswordMatch] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Real-time password validation using the new value
    if (name === 'password') {
      const requirements = validatePassword(value);
      setPasswordRequirements(requirements);
      // Use the current confirmPassword from state and the new password value
      setPasswordMatch(passwordsMatch(value, formData.confirmPassword));
    }
    
    // Real-time password match validation using the new value
    if (name === 'confirmPassword') {
      // Use the current password from state and the new confirmPassword value
      setPasswordMatch(passwordsMatch(formData.password, value));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({
      ...prev,
      celular: value || '',
    }));
    // Clear error for phone field
    if (errors.celular) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.celular;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.apellido.trim()) {
      newErrors.apellido = 'El apellido es requerido';
    }

    if (!formData.documento.trim()) {
      newErrors.documento = 'El documento es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(formData.email)) {
      newErrors.email = 'Correo electrónico inválido';
    } else {
      const domainCheck = checkEmailDomain(formData.email);
      if (domainCheck.blocked && domainCheck.suggestion) {
        setEmailSuggestion(domainCheck.suggestion);
        newErrors.email = `El dominio parece incorrecto. ¿Quisiste escribir ${domainCheck.suggestion}?`;
      } else {
        setEmailSuggestion(null);
      }
    }

    if (!formData.celular.trim()) {
      newErrors.celular = 'El celular es requerido';
    }

    if (!formData.genero) {
      newErrors.genero = 'El género es requerido';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (!passwordRequirements.allRequirementsMet) {
      newErrors.password = 'La contraseña no cumple con todos los requisitos de seguridad';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Debe confirmar la contraseña';
    } else if (!passwordMatch) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!turnstileToken) {
      newErrors.turnstile = 'Por favor complete la verificación de seguridad';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setSuccessMessage('');
    
    if (validateForm()) {
      setLoading(true);
      try {
        const registerData: RegisterData = {
          name: formData.nombre,
          surname: formData.apellido,
          document_number: formData.documento,
          email: formData.email,
          address: formData.domicilio,
          phone_number: formData.celular,
          birth_date: formData.birthdate,
          gender: formData.genero.charAt(0) as 'F' | 'M' | 'O', // Send only first character: F, M, or O
          password: formData.password,
          turnstile_token: turnstileToken,
        };
        
        await register(registerData);
        
        // Redirect to email verification page
        setSuccessMessage('Chequee su email para validar el usuario, ya puede cerrar esta ventana');
      } catch (error) {
        console.error('Registration error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
        setApiError(
          errorMessage || 'Error al registrar usuario. Por favor, intente nuevamente.'
        );
        // Reset turnstile on error
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
        setTurnstileToken('');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAcceptSuggestion = () => {
    if (!emailSuggestion) return;
    setFormData((prev) => ({ ...prev, email: emailSuggestion }));
    setEmailSuggestion(null);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  };

  const handleRejectSuggestion = () => {
    setFormData((prev) => ({ ...prev, email: '' }));
    setEmailSuggestion(null);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.email;
      return next;
    });
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="register-container">
      <div className="register-box">
        <div className="register-header">
          <h1 className="register-title">Crear Cuenta</h1>
          <p className="register-subtitle">Complete el formulario para registrarse</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nombre">Nombre *</label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Juan"
                disabled={loading}
                className={`form-input ${errors.nombre ? 'error' : ''}`}
              />
              {errors.nombre && <span className="error-text">{errors.nombre}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="apellido">Apellido *</label>
              <input
                id="apellido"
                name="apellido"
                type="text"
                value={formData.apellido}
                onChange={handleChange}
                placeholder="Pérez"
                disabled={loading}
                className={`form-input ${errors.apellido ? 'error' : ''}`}
              />
              {errors.apellido && <span className="error-text">{errors.apellido}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="documento">Documento *</label>
            <input
              id="documento"
              name="documento"
              type="text"
              value={formData.documento}
              onChange={handleChange}
              placeholder="DNI, Pasaporte, etc."
              disabled={loading}
              className={`form-input ${errors.documento ? 'error' : ''}`}
            />
            {errors.documento && <span className="error-text">{errors.documento}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Correo Electrónico *</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="ejemplo@correo.com"
              disabled={loading}
              className={`form-input ${errors.email ? 'error' : ''}`}
            />
            {errors.email && !emailSuggestion && (
              <span className="error-text">{errors.email}</span>
            )}
            {emailSuggestion && (
              <div className="email-suggestion">
                <span className="email-suggestion-text">{errors.email}</span>
                <div className="email-suggestion-actions">
                  <button
                    type="button"
                    onClick={handleAcceptSuggestion}
                    className="email-suggestion-btn email-suggestion-btn--accept"
                    disabled={loading}
                  >
                    Sí, corregir
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectSuggestion}
                    className="email-suggestion-btn email-suggestion-btn--reject"
                    disabled={loading}
                  >
                    No, cambiar manualmente
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña *</label>
            <div className="password-input-container">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 8 caracteres"
                disabled={loading}
                autoComplete="new-password"
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-button"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                disabled={loading}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña *</label>
            <div className="password-input-container">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repita su contraseña"
                disabled={loading}
                autoComplete="off"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle-button"
                aria-label={showConfirmPassword ? 'Ocultar confirmar contraseña' : 'Mostrar confirmar contraseña'}
                disabled={loading}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>

          {/* Password Requirements Indicator */}
          <div className="form-group">
            <PasswordRequirementsComponent 
              requirements={passwordRequirements}
              passwordsMatch={passwordMatch}
              showMatchRequirement={true}
            />
          </div>

          <div className="form-group">
            <label htmlFor="domicilio">Domicilio</label>
            <input
              id="domicilio"
              name="domicilio"
              type="text"
              value={formData.domicilio}
              onChange={handleChange}
              placeholder="Calle, Número, Ciudad"
              disabled={loading}
              className={`form-input ${errors.domicilio ? 'error' : ''}`}
            />
            {errors.domicilio && <span className="error-text">{errors.domicilio}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="celular">Celular *</label>
            <PhoneInput
              id="celular"
              international
              defaultCountry="AR"
              value={formData.celular}
              onChange={handlePhoneChange}
              placeholder="Ingrese número de teléfono"
              disabled={loading}
              className={`phone-input ${errors.celular ? 'error' : ''}`}
            />
            {errors.celular && <span className="error-text">{errors.celular}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="birthdate">Fecha de Nacimiento</label>
            <input
              id="birthdate"
              name="birthdate"
              type="date"
              value={formData.birthdate}
              onChange={handleChange}
              disabled={loading}
              className={`form-input ${errors.birthdate ? 'error' : ''}`}
            />
            {errors.birthdate && <span className="error-text">{errors.birthdate}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="genero">Género *</label>
            <select
              id="genero"
              name="genero"
              value={formData.genero}
              onChange={handleChange}
              disabled={loading}
              className={`form-input ${errors.genero ? 'error' : ''}`}
            >
              <option value="">Seleccione...</option>
              <option value="Femenino">Femenino</option>
              <option value="Masculino">Masculino</option>
              <option value="Otro">Otro</option>
            </select>
            {errors.genero && <span className="error-text">{errors.genero}</span>}
          </div>

          <div className="form-group turnstile-wrapper">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => {
                setTurnstileToken(token);
                if (errors.turnstile) {
                  setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.turnstile;
                    return newErrors;
                  });
                }
              }}
              onError={() => {
                setTurnstileToken('');
                setErrors((prev) => ({
                  ...prev,
                  turnstile: 'Error en la verificación de seguridad',
                }));
              }}
              onExpire={() => {
                setTurnstileToken('');
              }}
            />
            {errors.turnstile && <span className="error-text">{errors.turnstile}</span>}
          </div>
          {successMessage && <div className="api-success-message">{successMessage}</div>}
          {apiError && <div className="api-error-message">{apiError}</div>}

          <button type="submit" disabled={loading} className="register-button">
            {loading ? 'Registrando...' : 'Crear Cuenta'}
          </button>
        </form>

        <div className="register-footer">
          <p>
            ¿Ya tiene una cuenta?{' '}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="link-button"
            >
              Iniciar Sesión
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
